import * as React from "react";

import { Button, ButtonProps } from "@progress/kendo-react-buttons";
import { Loader } from "@progress/kendo-react-indicators";
import "./CPButtonStyles.scss";

export interface CPButtonProps extends ButtonProps {
  /** When `true`, replaces the button content with an infinite spinner and disables the button. Use while an async action is in progress. */
  showLoader?: boolean;
  /** When `true`, the button is not rendered at all (returns `null`). */
  hideButton?: boolean;
}

/**
 * Wrapper around the Kendo `Button` component with built-in loading and visibility helpers.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <CPButton themeColor="primary" onClick={handleSave}>Save</CPButton>
 *
 * // Show loading state while saving
 * <CPButton themeColor="primary" showLoader={isSaving} onClick={handleSave}>Save</CPButton>
 *
 * // Conditionally hide the button
 * <CPButton hideButton={!canEdit} onClick={handleEdit}>Edit</CPButton>
 * ```
 */
const CPButton: React.FC<CPButtonProps> = ({
  showLoader,
  children,
  hideButton,
  ...rest
}) => {
  showLoader = showLoader ?? false;

  if (hideButton) {
    return null;
  }

  return showLoader ? (
    <Button type={"button"} {...rest} disabled={true} icon={""}>
      <Loader type={"infinite-spinner"} size="small" />
    </Button>
  ) : (
    <Button type={"button"} {...rest}>
      {children}
    </Button>
  );
};

export default CPButton;
